import trySaveMessage from '../../actions/trySaveMessage';
import createSignatureBlock from '../../utils/createSignatureBlock';
import getSignature from '../../utils/getSignature';
import { isViewStateDirty } from '../../utils/viewStateUtils';
import { IContentOperationPlugin } from 'owa-editor-plugins-list';
import updateContent from 'owa-editor/lib/actions/updateContent';
import { ComposeViewStateAdapter } from 'owa-mail-compose-actions/lib/utils/createPluginViewStateAdapter';
import BodyType from 'owa-service/lib/contract/BodyType';
import { Editor } from 'roosterjs-editor-core';
import {
    ContentPosition,
    PluginEvent,
    PluginEventType,
    PositionType,
} from 'roosterjs-editor-types';

interface CursorSnapshot {
    snapshot?: string;
    htmlSnapshotWithCursorMarker?: string;
}

interface InsertContentOption {
    content: string;
    eventName: string;
    belowExistingContent: boolean;
    setFocusToEditor: boolean;
}

const OWA_REFERENCE_ATTACHMENT_SELECTOR = '#OwaReferenceAttachments';
const OWA_REFERENCE_ATTACHMENT_END_SELECTOR = '#OwaReferenceAttachmentsEnd';

export default class ContentOperationPlugin implements IContentOperationPlugin {
    private editor: Editor;
    private plainTextEditor: HTMLTextAreaElement;
    private saveOnDispose: boolean;
    private cursorSnapshot: CursorSnapshot;
    private replyWithTextElementId: string;
    private hiddenAttachmentsHtml: string = '';
    private insertContentOption: InsertContentOption = null;
    private focusOnReady: boolean;

    constructor(private adapter: ComposeViewStateAdapter) {}

    public getName() {
        return 'ContentOperation';
    }

    public initialize(editor: Editor) {
        this.editor = editor;
        this.saveOnDispose = true;
    }

    public dispose() {
        if (this.saveOnDispose) {
            const viewState = this.adapter();
            this.cursorSnapshot = {};
            if (this.editor && this.editor.getSelectionRange()) {
                this.cursorSnapshot.htmlSnapshotWithCursorMarker = this.editor.getContent(
                    false /*triggerExtractContentEvent*/,
                    true /*includeSelectionMarker*/
                );
            }

            this.updateContentToViewState();
            this.cursorSnapshot.snapshot = viewState.content;

            if (isViewStateDirty(viewState)) {
                trySaveMessage(viewState, true /*isAutoSave*/);
            }
        }

        this.saveOnDispose = false;
        this.editor = null;
    }

    public hasCursorSnapshot(): boolean {
        return this.cursorSnapshot && !!this.cursorSnapshot.htmlSnapshotWithCursorMarker;
    }

    public focusToEditor() {
        this.focusOnReady = false;

        if (this.editor) {
            this.editor.focus();
            if (
                this.hasCursorSnapshot() &&
                this.cursorSnapshot.snapshot == this.adapter().content
            ) {
                this.editor.setContent(this.cursorSnapshot.htmlSnapshotWithCursorMarker);
                this.editor.triggerContentChangedEvent('RestoreCursor');
            }
            this.cursorSnapshot = null;
        } else if (this.plainTextEditor) {
            this.plainTextEditor.focus();
            this.plainTextEditor.selectionStart = 0;
            this.plainTextEditor.selectionEnd = 0;
        } else {
            this.focusOnReady = true;
        }
    }

    public stopSaveOnDispose() {
        this.saveOnDispose = false;
    }

    public updateContentToViewState() {
        const content = this.editor
            ? this.editor.getContent(true /*triggerExtractContentEvent*/)
            : this.plainTextEditor
            ? this.plainTextEditor.value
            : null;

        if (content != null) {
            updateContent(this.adapter(), content);
        }
    }

    public setPlainTextEditor(plainTextEditor: HTMLTextAreaElement) {
        this.plainTextEditor = plainTextEditor;
        if (this.plainTextEditor) {
            if (this.insertContentOption) {
                this.retryInsertContentIntoEditor();
            }
            if (this.focusOnReady) {
                this.focusToEditor();
            }
        }
    }

    public onPluginEvent(event: PluginEvent): void {
        if (event.eventType === PluginEventType.EditorReady) {
            // If the editor body contains hidden attachment helper nodes, temporarily remove them,
            // and we will add them back when retrieve the HTML content.
            const divOwaReferenceAttachments = this.editor.queryElements(
                OWA_REFERENCE_ATTACHMENT_SELECTOR
            )[0];
            const divOwaReferenceAttachmentsEnd = this.editor.queryElements(
                OWA_REFERENCE_ATTACHMENT_END_SELECTOR
            )[0];

            if (
                divOwaReferenceAttachments &&
                !divOwaReferenceAttachments.previousElementSibling &&
                divOwaReferenceAttachmentsEnd &&
                divOwaReferenceAttachmentsEnd.previousElementSibling == divOwaReferenceAttachments
            ) {
                this.hiddenAttachmentsHtml =
                    (divOwaReferenceAttachments ? divOwaReferenceAttachments.outerHTML : '') +
                    (divOwaReferenceAttachmentsEnd ? divOwaReferenceAttachmentsEnd.outerHTML : '');
                this.editor.deleteNode(divOwaReferenceAttachments);
                this.editor.deleteNode(divOwaReferenceAttachmentsEnd);
            }

            if (this.insertContentOption) {
                this.retryInsertContentIntoEditor();
            }

            if (this.replyWithTextElementId) {
                // Wait until the text element has been inserted before moving the caret
                this.moveCaretBelowHtmlElement(this.replyWithTextElementId);
            }

            if (this.focusOnReady) {
                this.focusToEditor();
            }
        } else if (event.eventType === PluginEventType.ExtractContent) {
            // Add the attachment helper nodes back
            event.content = this.hiddenAttachmentsHtml + event.content;
        }
    }

    public insertSignature(bodyType: BodyType) {
        const signatureText = createSignatureBlock(bodyType, getSignature(bodyType));

        if (bodyType == 'HTML' && this.editor) {
            this.editor.focus();
            this.editor.addUndoSnapshot();
            this.editor.insertContent(signatureText, {
                position: ContentPosition.SelectionStart,
                updateCursor: false,
                replaceSelection: true,
                insertOnNewLine: true,
            });
            this.editor.triggerContentChangedEvent('Signature');
            this.editor.addUndoSnapshot();
        } else if (bodyType == 'Text' && this.plainTextEditor) {
            const cursorPosition = this.plainTextEditor.selectionEnd;
            const newContent =
                this.plainTextEditor.value.substr(0, cursorPosition) +
                signatureText +
                this.plainTextEditor.value.substr(cursorPosition);
            this.plainTextEditor.value = newContent;
            this.updateContentToViewState();
        }
    }

    public moveCaretBelowHtmlElement(elementId: string) {
        if (!this.editor) {
            // The first time this method is called, the editor is not yet initialized and the
            // quote element has not yet been inserted
            // Remember the element's ID for now, and this function will be called again later
            this.replyWithTextElementId = elementId;
            return;
        }

        this.placeCursorAfterElement(elementId);
    }

    public isEmpty(): boolean {
        return this.editor && this.editor.isEmpty();
    }

    public removeInlineImage() {
        this.editor.queryElements('img', image => {
            this.editor.deleteNode(image);
        });
    }

    public getTextContent(): string {
        return this.editor && this.editor.getTextContent();
    }

    public insertContentIntoEditor(
        content: string,
        eventName: string,
        belowExistingContent: boolean,
        setFocusToEditor: boolean
    ) {
        const isHtml = this.adapter().bodyType == 'HTML';
        if ((isHtml && !this.editor) || (!isHtml && !this.plainTextEditor)) {
            // If this method has been called before the editor has been initiailized,
            // save the params for now and try again once the editor is loaded (VSO 54795)
            this.insertContentOption = {
                content,
                eventName,
                belowExistingContent,
                setFocusToEditor,
            };
        }

        if (isHtml && this.editor) {
            if (setFocusToEditor) {
                this.editor.focus();
            }

            this.editor.addUndoSnapshot();

            this.editor.insertContent(content, {
                insertOnNewLine: belowExistingContent,
                position: belowExistingContent
                    ? ContentPosition.End
                    : ContentPosition.SelectionStart,
                replaceSelection: false,
                updateCursor: !belowExistingContent,
            });

            this.editor.triggerContentChangedEvent(eventName);
            this.editor.addUndoSnapshot();
        } else if (!isHtml && this.plainTextEditor) {
            const start = this.plainTextEditor.selectionStart;
            const end = this.plainTextEditor.selectionEnd;

            if (belowExistingContent) {
                this.plainTextEditor.value += content;
                this.plainTextEditor.selectionStart = start;
                this.plainTextEditor.selectionEnd = end;
            } else {
                this.plainTextEditor.value =
                    this.plainTextEditor.value.substr(0, start) +
                    content +
                    this.plainTextEditor.value.substr(end);
                this.plainTextEditor.selectionStart = start + content.length;
                this.plainTextEditor.selectionEnd = end + content.length;
            }

            if (setFocusToEditor) {
                this.plainTextEditor.focus();
            }
        }

        // We depend on ReactEditor updating the viewState's content property onBlur.
        // Therefore, we manually set the viewState's content property here.
        // If this changes, this will need to be updated.
        if (!setFocusToEditor || !isHtml) {
            this.updateContentToViewState();
        }
    }

    private retryInsertContentIntoEditor() {
        const {
            content,
            eventName,
            belowExistingContent,
            setFocusToEditor,
        } = this.insertContentOption;

        this.insertContentIntoEditor(content, eventName, belowExistingContent, setFocusToEditor);
        if (setFocusToEditor) {
            this.focusToEditor();
        }

        this.insertContentOption = null;
    }

    private placeCursorAfterElement(elementId: string) {
        const element = this.editor.getDocument().getElementById(elementId);
        if (element) {
            this.editor.focus();
            this.editor.select(element, PositionType.After);
        }
    }
}
